export interface RouteResult {
  coordinates: Array<[number, number]>;
  distance: number;
  duration: number;
  instructions: string[];
  polyline?: any;
}

export async function calculateOptimalRoute(
  startPoint: { lat: number; lng: number },
  waypoints: Array<{ lat: number; lng: number }>
): Promise<RouteResult> {
  try {
    // Get API key from environment
    const apiKey = process.env.REACT_APP_OPENROUTE_API_KEY;
    
    // Create the coordinate string for OpenRouteService
    const coordinates = [
      [startPoint.lng, startPoint.lat],
      ...waypoints.map(point => [point.lng, point.lat]),
      [startPoint.lng, startPoint.lat] // Return to start
    ];

    // Try OpenRouteService if API key is available
    if (apiKey && apiKey !== 'your_openroute_api_key_here') {
      try {
        const response = await fetch('https://api.openrouteservice.org/v2/directions/driving-car/geojson', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': apiKey
          },
          body: JSON.stringify({
            coordinates: coordinates,
            options: {
              optimize_waypoints: true
            }
          })
        });

        if (response.ok) {
          const data = await response.json();
          const route = data.features[0];
          
          return {
            coordinates: route.geometry.coordinates.map((coord: number[]) => [coord[1], coord[0]]), // Swap lng,lat to lat,lng
            distance: route.properties.segments.reduce((total: number, seg: any) => total + seg.distance, 0),
            duration: route.properties.segments.reduce((total: number, seg: any) => total + seg.duration, 0),
            instructions: route.properties.segments.flatMap((seg: any) => 
              seg.steps.map((step: any) => step.instruction)
            )
          };
        }
      } catch (error) {
        console.warn('OpenRouteService failed, trying OSRM:', error);
      }
    }

    // Try OSRM (free alternative, no API key required)
    try {
      console.log('Attempting to use OSRM routing service...');
      return await calculateOSRMRoute(startPoint, waypoints);
    } catch (error) {
      console.warn('OSRM failed, trying GraphHopper:', error);
    }

    // Try GraphHopper (free tier available)
    try {
      console.log('Attempting to use GraphHopper routing service...');
      return await calculateGraphHopperRoute(startPoint, waypoints);
    } catch (error) {
      console.warn('GraphHopper failed, using fallback routing:', error);
    }

    // Fallback to simple straight-line routing
    console.log('Using fallback routing (all services unavailable)');
    return createFallbackRoute(startPoint, waypoints);

  } catch (error) {
    console.error('Routing error:', error);
    return createFallbackRoute(startPoint, waypoints);
  }
}

// OSRM routing (free, no API key required)
async function calculateOSRMRoute(
  startPoint: { lat: number; lng: number },
  waypoints: Array<{ lat: number; lng: number }>
): Promise<RouteResult> {
  // For OSRM, we need to make separate requests for each segment
  // since it doesn't have built-in waypoint optimization
  const allPoints = [startPoint, ...waypoints, startPoint];
  const allCoordinates: Array<[number, number]> = [];
  let totalDistance = 0;
  let totalDuration = 0;
  const instructions: string[] = [];

  for (let i = 0; i < allPoints.length - 1; i++) {
    const from = allPoints[i];
    const to = allPoints[i + 1];
    
    const coordinatesString = `${from.lng},${from.lat};${to.lng},${to.lat}`;
    const url = `https://router.project-osrm.org/route/v1/driving/${coordinatesString}?overview=full&geometries=geojson`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`OSRM request failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.routes && data.routes.length > 0) {
      const route = data.routes[0];
      
      // Add coordinates (skip first point of subsequent segments to avoid duplication)
      const segmentCoords = route.geometry.coordinates.map((coord: number[]) => [coord[1], coord[0]] as [number, number]);
      if (i === 0) {
        allCoordinates.push(...segmentCoords);
      } else {
        allCoordinates.push(...segmentCoords.slice(1));
      }
      
      totalDistance += route.distance;
      totalDuration += route.duration;
      
      // Add basic instructions
      if (route.legs && route.legs.length > 0) {
        instructions.push(`Segment ${i + 1}: ${(route.distance / 1000).toFixed(1)}km`);
      }
    }
  }

  return {
    coordinates: allCoordinates,
    distance: totalDistance,
    duration: totalDuration,
    instructions: instructions
  };
}

// GraphHopper routing (free tier available)
async function calculateGraphHopperRoute(
  startPoint: { lat: number; lng: number },
  waypoints: Array<{ lat: number; lng: number }>
): Promise<RouteResult> {
  // GraphHopper public API (limited requests)
  const allPoints = [startPoint, ...waypoints, startPoint];
  const pointsParam = allPoints.map(p => `${p.lat},${p.lng}`).join('&point=');
  const url = `https://graphhopper.com/api/1/route?point=${pointsParam}&vehicle=car&locale=en&calc_points=true&type=json`;
  
  const response = await fetch(url);
  
  if (!response.ok) {
    throw new Error(`GraphHopper request failed: ${response.status}`);
  }
  
  const data = await response.json();
  
  if (data.paths && data.paths.length > 0) {
    const route = data.paths[0];
    
    // Decode the polyline points
    const coordinates = route.points.coordinates.map((coord: number[]) => [coord[1], coord[0]] as [number, number]);
    
    return {
      coordinates: coordinates,
      distance: route.distance,
      duration: route.time / 1000, // Convert from milliseconds to seconds
      instructions: route.instructions ? route.instructions.map((inst: any) => inst.text) : []
    };
  }
  
  throw new Error('No routes found from GraphHopper');
}

// Helper function to create coordinate tuple
const createCoordinate = (lat: number, lng: number): [number, number] => [lat, lng];

// Fallback to simple straight-line routing
function createFallbackRoute(
  startPoint: { lat: number; lng: number },
  waypoints: Array<{ lat: number; lng: number }>
): RouteResult {
  const coordinates: Array<[number, number]> = [
    [startPoint.lat, startPoint.lng] as [number, number],
    ...waypoints.map(point => [point.lat, point.lng] as [number, number]),
    [startPoint.lat, startPoint.lng] as [number, number]
  ];

  // Calculate approximate distance (straight line)
  let totalDistance = 0;
  for (let i = 0; i < coordinates.length - 1; i++) {
    const distance = calculateDistance(
      coordinates[i][0], coordinates[i][1],
      coordinates[i + 1][0], coordinates[i + 1][1]
    );
    totalDistance += distance;
  }

  return {
    coordinates,
    distance: totalDistance * 1000, // Convert to meters to match API responses
    duration: totalDistance / 50 * 3600, // Assume 50 km/h average speed
    instructions: waypoints.map((_, i) => `Go to waypoint ${i + 1}`)
  };
}

// Haversine formula for distance calculation
function calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}